import * as React from 'react'

import { Container } from '../../_shared/Container'
import { SectionHead, SectionHeadHighlight } from '../../_shared/SectionHead'
import { formatInteger, type UluleStats } from '../../_shared/ululeStats'
import { CountUp } from '../components/CountUp'
import { type Collection } from '../collections'
import { MONEY } from '../content'
import { FIGURES } from '../figures'
import styles from './Money.module.css'

type Figure = { key: string; value: number; body: React.ReactNode }

/**
 * The average contribution is live (`amount_avg.EUR`) and the largest collection is the head of
 * the all-time board, so the two always agree. The smallest has no source and is hand-maintained
 * in `figures.json`. Each figure is dropped rather than faked when its source is missing.
 */
function makeFigures(stats: UluleStats | null, topAllTime: Collection[]): Figure[] {
  const average =
    stats?.amountAvgEur === undefined
      ? []
      : [{ key: 'average', value: Math.round(stats.amountAvgEur), body: MONEY.averageBody }]

  const [biggest] = topAllTime
  const largest =
    biggest === undefined
      ? []
      : [
          {
            key: 'largest',
            value: biggest.amountEur,
            body: (
              <>
                {MONEY.largestBodyBefore}
                <a href="#top10">{MONEY.largestBodyLink}</a>
                {MONEY.largestBodyAfter}
              </>
            ),
          },
        ]

  return [...average, { key: 'smallest', value: FIGURES.smallestSuccessEur, body: MONEY.smallestBody }, ...largest]
}

export function Money({
  stats,
  topAllTime,
}: {
  stats: UluleStats | null
  topAllTime: Collection[]
}): React.ReactElement {
  const figures = makeFigures(stats, topAllTime)

  return (
    <section className={styles.money}>
      <Container>
        <SectionHead
          size="wide"
          className={styles.money__head}
          kicker={MONEY.kicker}
          title={
            <>
              {'Des chiffres '}
              <SectionHeadHighlight>qui comptent</SectionHeadHighlight>
            </>
          }
        />
        <div className={styles.money__grid}>
          {figures.map((figure) => (
            <div key={figure.key} className={styles.mn}>
              <div className={styles.mn__num}>
                {/* NO-BREAK space before the unit: French typography requires it, the hero and
                    the category ledger already set their amounts that way, and this figure's box
                    has no `white-space: nowrap` to keep a plain space from wrapping the sign. */}
                <CountUp value={figure.value} suffix={' €'} format={formatInteger} />
              </div>
              <p className={styles.mn__body}>{figure.body}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}
